import React, { useEffect, useState } from "react";
import Modal from "@components/atoms/Modal";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import { Button, VDBText } from "@components/atoms";
import styled, { useTheme } from "styled-components/native";
import { SpaceType } from "@components/atoms/Spacer/types";
import { ShareExcelModalProps } from "@components/molecules/Share/utils/utils";
import { useTypographyStore } from "@redux/slices/typographySlice";
import Spacer from "@components/atoms/Spacer";
import RadioButton from "@components/atoms/RadioButton";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import { ButtonCategory } from "@components/atoms/Button/types";
import { css } from "styled-components/native";
import { useAppSettings } from "@redux/slices/globalSlice";

export enum ShareMode {
  EXPORT_SELECTED_ITEMS = "export_selected_items",
  EXPORT_SEARCHED_ITEMS = "export_searched_items",
}

export enum FileFormat {
  CSV = "xlsx",
  PDF = "pdf",
}

const DownloadCSVModal = ({
  isOpen,
  onClose,
  enableToastMessage,
  selectedItemsCount = 0,
  onSendEmail,
  fromFavoriteScreen = false,
}: ShareExcelModalProps & {
  selectedItemsCount: number;
  onSendEmail: (format: FileFormat, mode: ShareMode) => void;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const typography = useTypographyStore();
  const maxItems = useAppSettings()?.maximum_exports_number;
  const [shareMode, setShareMode] = useState<ShareMode>(
    selectedItemsCount > 0
      ? ShareMode.EXPORT_SELECTED_ITEMS
      : ShareMode.EXPORT_SEARCHED_ITEMS
  );

  // const [fileFormat, setFileFormat] = useState<FileFormat>(FileFormat.CSV);

  const handleClose = () => {
    onClose();
  };

  const handleSend = () => {
    onSendEmail(FileFormat.CSV, shareMode);
  };

  useEffect(() => {
    if (selectedItemsCount === 0) {
      setShareMode(ShareMode.EXPORT_SEARCHED_ITEMS);
    } else if (isOpen) {
      setShareMode(ShareMode.EXPORT_SELECTED_ITEMS);
    }
  }, [selectedItemsCount, isOpen]);

  return (
    <Modal
      headerTitle={`${t("export.modal.title.text", "Download CSV / PDF")}${
        selectedItemsCount > 0 ? ` (${selectedItemsCount})` : ""
      }`}
      isOpen={isOpen}
      onClose={handleClose}
      paddingLeft={16}
      paddingRight={16}
      paddingBottom={32}
      paddingTop={16}
      enableToastMessage={enableToastMessage}
      size={375}
    >
      <ContentWrapper>
        <Container>
          {!fromFavoriteScreen && (
            <>
              <RadioButtonContainer>
                <RadioButton
                  label={`${
                    (t("export.modal.selectedItems"), "Selected Items")
                  } (${selectedItemsCount})`}
                  testID="export.radio.selectedItems"
                  dataTestId="export.radio.selectedItems"
                  labelColor={
                    shareMode === ShareMode.EXPORT_SELECTED_ITEMS
                      ? theme["core-05"]
                      : theme["core-06"]
                  }
                  styles={
                    shareMode === ShareMode.EXPORT_SELECTED_ITEMS
                      ? typography["Body-M"]
                      : typography["Body-R"]
                  }
                  checked={shareMode === ShareMode.EXPORT_SELECTED_ITEMS}
                  onPress={() => {
                    if (selectedItemsCount > 0) {
                      setShareMode(ShareMode.EXPORT_SELECTED_ITEMS);
                    }
                  }}
                  margin={"0"}
                  disabled={selectedItemsCount === 0}
                  buttonColor={
                    selectedItemsCount === 0
                      ? theme["core-03"]
                      : theme["knob-bor"]
                  }
                />
                <RadioButton
                  label={t(
                    "export.modal.allSearchedItems",
                    "All Searched Items"
                  )}
                  testID="export.radio.allItems"
                  dataTestId="export.radio.allItems"
                  labelColor={
                    shareMode === ShareMode.EXPORT_SEARCHED_ITEMS
                      ? theme["core-05"]
                      : theme["core-06"]
                  }
                  styles={
                    shareMode === ShareMode.EXPORT_SEARCHED_ITEMS
                      ? typography["Body-M"]
                      : typography["Body-R"]
                  }
                  checked={shareMode === ShareMode.EXPORT_SEARCHED_ITEMS}
                  margin={"0"}
                  onPress={() => setShareMode(ShareMode.EXPORT_SEARCHED_ITEMS)}
                />
              </RadioButtonContainer>
              <Spacer size={SpaceType.Big} />
            </>
          )}
          <WarningContainer>
            <VDBIcon
              name={VDBIcons.WARNING}
              variant={IconsVariant.VDBCustom}
              size={20}
              color={theme["core-04"]}
            />
            <VDBText type={typography["Body-R"]} color={theme["core-06"]}>
              {t(
                "export.modal.noteMaxItems",
                "Note: Up to {{count}} items can be downloaded",
                { count: maxItems ?? 10000 }
              )}
            </VDBText>
          </WarningContainer>
          {/* <Spacer size={SpaceType.Big} />
          <FileFormatContainer>
            <VDBText type={typography["Body-R"]}>
              {t("export.modal.selectFileFormat", "Select the file format")}
            </VDBText>
            <RadioButtonContainer>
              <RadioButton
                label={t("export.modal.csv", "CSV")}
                styles={
                  fileFormat === FileFormat.CSV
                    ? typography["Body-M"]
                    : typography["Body-R"]
                }
                labelColor={
                  fileFormat === FileFormat.CSV
                    ? theme["core-05"]
                    : theme["core-06"]
                }
                checked={fileFormat === FileFormat.CSV}
                margin={"0"}
                onPress={() => setFileFormat(FileFormat.CSV)}
              />
              <RadioButton
                label={t("export.modal.pdf", "PDF")}
                styles={
                  fileFormat === FileFormat.PDF
                    ? typography["Body-M"]
                    : typography["Body-R"]
                }
                labelColor={
                  fileFormat === FileFormat.PDF
                    ? theme["core-05"]
                    : theme["core-06"]
                }
                checked={fileFormat === FileFormat.PDF}
                onPress={() => {
                  setFileFormat(FileFormat.PDF);
                }}
                margin={"0"}
              />
            </RadioButtonContainer>
          </FileFormatContainer> */}
          <Spacer size={SpaceType.Big} />
          <Button
            title={t("export.modal.downloadButton", "Download")}
            testID="export.button.download"
            dataTestId="export.button.download"
            onPress={handleSend}
            category={ButtonCategory.default}
          />
          <Spacer size={SpaceType.Big} />
        </Container>
      </ContentWrapper>
    </Modal>
  );
};

const Container = styled(View)`
  padding: ${SpaceType.ExtraSmall}px;
  ${({ theme }) =>
    theme.isTablet &&
    css`
      width: 60%;
      max-width: 600px;
    `}
`;

const ContentWrapper = styled(View)`
  ${({ theme }) =>
    theme.isTablet &&
    css`
      align-items: center;
      width: 100%;
    `}
`;

const RadioButtonContainer = styled.View`
  gap: ${SpaceType.Double}px;
`;

const WarningContainer = styled.View`
  flex-direction: row;
  align-items: center;
  gap: ${SpaceType.Basic}px;
  background-color: ${({ theme }) => theme["core-02"]};
  border-radius: ${SpaceType.Basic}px;
  height: ${SpaceType.ExtraLarge}px;
  padding: ${SpaceType.Extra}px;
`;

// const FileFormatContainer = styled.View`
//   gap: ${SpaceType.Basic}px;
// `;

export default DownloadCSVModal;
